import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle } from "lucide-react";
import { FilePreviewInline } from "@/components/file-preview/FilePreviewInline";
import { useQuestionnaireContext } from "../../context/QuestionnaireContext";
import { useQuestionnaireData } from "../../hooks";

export function QuestionnaireSummaryStep() {
  const { t } = useTranslation();
  const { summaryPdfBase64, handleAcceptAgreement, handleCancelAgreement } =
    useQuestionnaireContext();
  const { canBeRejectedByContractor } = useQuestionnaireData();

  const previewFile = useMemo(() => {
    if (!summaryPdfBase64) return null;
    return {
      filename: t("questionnaires.summary.agreementPreview"),
      mimeType: "application/pdf",
      url: `data:application/pdf;base64,${summaryPdfBase64}`,
    };
  }, [summaryPdfBase64, t]);

  return (
    <div className="h-screen flex flex-col bg-white">
      <div className="flex flex-col h-full mx-auto w-full max-w-3xl border-x border-slate-200 bg-white">
        <div className="px-4 py-1 sm:px-6">
          <h2 className="text-sm py-2 sm:text-xl">
            {t("questionnaires.summary.agreementPreview")}
          </h2>
        </div>
        <div className="flex-1 flex flex-col min-h-0">
          {previewFile && (
            <div className="flex-1 min-h-0 border-t border-b border-slate-200">
              <FilePreviewInline
                fileUrl={previewFile.url}
                fileName={previewFile.filename}
                mimeType={previewFile.mimeType}
                fullPage={true}
              />
            </div>
          )}
        </div>

        {/* ACTIONS — sticky bottom */}
        <div className="sticky bottom-0 bg-white p-3 sm:p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            {canBeRejectedByContractor && (
              <Button
                variant="destructive"
                size="lg"
                className="flex-1 h-auto py-2 sm:py-2.5"
                onClick={handleCancelAgreement}
              >
                <XCircle className="mr-2 h-4 w-4" />
                {t("questionnaires.summary.actions.reject")}
              </Button>
            )}
            <Button
              size="lg"
              className="flex-1 bg-green-600 hover:bg-green-700 h-auto py-2 sm:py-2.5"
              onClick={handleAcceptAgreement}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              {t("questionnaires.summary.actions.accept")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
